import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MarkantElementsModule, MarkantGuidedTour } from '@markant/elements-angular';
import { HighlightDescription, HighlightDescriptionButton } from '../model/highlight-description';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { GuidedTourStep } from '../model/guided-tour-step';
import { GuidedTourButton, GuidedTourTarget } from '../model/mel-guided-tour-types';
import { NgIf } from '@angular/common';

@Component({
  selector: 'markant-platform-guided-tour-wrapper',
  standalone: true,
  imports: [MarkantElementsModule, TranslocoModule, NgIf],
  templateUrl: './guided-tour-wrapper.component.html',
  styleUrl: './guided-tour-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuidedTourWrapperComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) guidedTourSteps!: GuidedTourStep[];
  @Input({ required: true }) onNext!: () => Promise<void>;
  @Input({ required: true }) onBack!: () => Promise<void>;
  @Input({ required: true }) currentStep!: number;
  @Input() enableClickThrough = true;

  @ViewChild('guidedTourElement', { static: true }) guidedTourElement!: MarkantGuidedTour;

  guidedTourTarget: GuidedTourTarget[] = [];
  nextButton!: GuidedTourButton;
  backButton!: GuidedTourButton;
  highlightButtonListeners: { button: HTMLElement; event: () => void }[] = [];

  private ignoreHighlightClick: boolean[] = [];
  private clickTargets: string[] = [];
  private markantGuideArrowObserver!: MutationObserver;

  constructor(
    private translocoService: TranslocoService,
    private renderer: Renderer2
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['guidedTourSteps']) {
      this.clickTargets = [];
      this.ignoreHighlightClick = [];

      this.guidedTourTarget = this.guidedTourSteps.map(step => {
        this.clickTargets.push(step.clickTargetId || step.highlightTargetId);
        this.ignoreHighlightClick.push(!!step.ignoreHighlightClick);

        return {
          id: step.highlightTargetId,
          description: this.parseDescription(step.description),
          shape: step.shape || 'circle',
          border: step.border || 0,
          arrowAttachment: step.arrowAttachment || 'auto',
        };
      });

      this.nextButton = { active: false, functionOverride: this.onNext };
      this.backButton = {
        active: true,
        functionOverride: this.onBack,
        defaultText: this.translocoService.translate('guidedTour.navigationButtons.back'),
        endText: this.translocoService.translate('guidedTour.navigationButtons.cancel'),
      };
    }
  }

  ngOnDestroy(): void {
    this.removeHighlightButtonListeners();

    if (this.markantGuideArrowObserver) {
      this.markantGuideArrowObserver.disconnect();
    }
  }

  private parseDescription(description?: HighlightDescription): string | undefined {
    if (!description) {
      return undefined;
    }
    const buttonString = description.buttons ? this.createButtonsString(description.buttons) : '';
    return `${this.translocoService.translate(description.text)}${buttonString}`;
  }

  private createButtonsString(buttons: HighlightDescriptionButton[]): string {
    if (!buttons || buttons.length === 0) {
      return '';
    }

    const buttonsString = buttons
      .map(button => {
        const buttonText = this.translocoService.translate(button.text);
        let icon = '';

        if (button.icon) {
          icon =
            '<markant-icon ' +
            `slot="markant-button-icon-${button.icon.position}" ` +
            `icon="${button.icon.id}"` +
            '></markant-icon>';
        }

        return (
          '<markant-button ' +
          `id="${button.id}" ` +
          'class="tour-highlight-button" ' +
          `color-scheme="${button.colorScheme || 'primary'}">` +
          `${buttonText}${icon}` +
          '</markant-button>'
        );
      })
      .join('');

    return `<br/><div class="highlight-button-row">${buttonsString}</div>`;
  }

  @HostListener('document:click', ['$event'])
  async handleTourOverlayClicks(event: MouseEvent): Promise<void> {
    // provides a callback for the current highlighted target element of the guided tour
    const clickTarget = event.target as HTMLElement;

    // Check if the click was on the current target
    const curTargetSelector = `#${this.clickTargets[this.currentStep - 1]}`;
    if (clickTarget.matches(curTargetSelector) || clickTarget.closest(curTargetSelector)) {
      if (!this.ignoreHighlightClick[this.currentStep - 1]) {
        await this.onNext();
      }
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.onChanged();
  }

  async start(): Promise<void> {
    await this.guidedTourElement.start();
    this.observeMarkantGuideArrow();
  }

  async next(withTimeOut = false): Promise<void> {
    const currentSelector = `#${this.guidedTourTarget[this.currentStep - (withTimeOut ? 1 : 0)]?.id}`;
    const targetExists = await this.targetIsInDOM(currentSelector);

    if (targetExists) {
      await this.guidedTourElement.next();
    }
  }

  async end(): Promise<void> {
    await this.guidedTourElement.end();
    this.removeHighlightButtonListeners();
  }

  async jumpTo(target: number): Promise<void> {
    const targetSelector = `#${this.guidedTourTarget[target]?.id}`;
    const targetExists = await this.targetIsInDOM(targetSelector);

    if (targetExists) {
      await this.guidedTourElement.jumpTo(target);
      setTimeout(async () => await this.onChanged(target));
    }
  }

  async onChanged(targetIndex?: number): Promise<void> {
    const currentStep = targetIndex ?? this.currentStep - 1;
    const currentStepDef = this.guidedTourSteps[currentStep];

    if (currentStepDef) {
      const backButtonElement = await this.targetIsInDOM('#elements-guided-tour-back-button');
      if (currentStepDef.hideBackButton) {
        this.renderer.addClass(backButtonElement, 'hidden');
      } else {
        this.renderer.removeClass(backButtonElement, 'hidden');
      }

      const currentStepDescription = currentStepDef.description;

      if (currentStepDescription && currentStepDescription.buttons) {
        this.addHighlightButtonClickListener(currentStepDescription.buttons);
      }
    }
  }

  private observeMarkantGuideArrow(): void {
    const highlighter = document.querySelector('#elements-highlighter') as HTMLElement;

    this.markantGuideArrowObserver = new MutationObserver((mutations: MutationRecord[], observer: MutationObserver) =>
      this.arrowMutationObserverCallback(mutations, observer)
    );

    this.markantGuideArrowObserver.observe(highlighter, {
      childList: true,
    });
  }

  private arrowMutationObserverCallback(mutations: MutationRecord[], observer: MutationObserver): MutationObserver {
    const currentStepDef = this.guidedTourSteps[this.currentStep - 1];
    const mutationList = mutations.filter(mutation => mutation.type === 'childList');

    const lastMutation = mutationList[mutationList.length - 1].addedNodes[0];

    if (currentStepDef.hideArrow) {
      this.renderer.addClass(lastMutation, 'hidden');
    } else {
      this.renderer.removeClass(lastMutation, 'hidden');
    }

    return observer;
  }

  private addHighlightButtonClickListener(highlightButtons: HighlightDescriptionButton[]): void {
    const highlightTextBox = document.querySelector('#elements-highlighter') as HTMLElement;
    const config = { childList: true };

    const mutationObserver = new MutationObserver((mutationList, observer) => {
      const mutation = mutationList.filter(
        mutation =>
          mutation.type === 'childList' &&
          mutation.addedNodes.length > 0 &&
          (<HTMLDivElement>mutation.addedNodes.item(0)).id === 'elements-highlight-text-box'
      );

      if (mutation.length > 0) {
        const highlightText = mutation[mutation.length - 1].addedNodes.item(0) as Node;
        let buttonRowElement: Node | null = null;

        highlightText.childNodes.forEach((node: Node) => {
          if ((<HTMLDivElement>node).classList?.contains('highlight-button-row')) {
            buttonRowElement = node;
          }
        });

        if (buttonRowElement) {
          observer.disconnect();
          this.highlightButtonListeners.forEach(listener => {
            listener.button.removeEventListener('click', listener.event);
          });
          this.highlightButtonListeners = [];
          (<Node>buttonRowElement).childNodes.forEach((node: Node) => {
            const button = node as HTMLElement;
            const highlightButton = highlightButtons.find(buttonDefinition => buttonDefinition.id === button.id);

            if (highlightButton) {
              const eventCallback = highlightButton.callback;
              button.addEventListener('click', eventCallback);
              this.highlightButtonListeners.push({ button, event: eventCallback });
            }
          });
        }
      }
    });

    mutationObserver.observe(highlightTextBox, config);
  }

  private removeHighlightButtonListeners(): void {
    this.highlightButtonListeners.forEach(event => {
      event.button.removeEventListener('click', event.event);
    });

    this.highlightButtonListeners = [];
  }

  private targetIsInDOM(target: string): Promise<Element> {
    const config = { childList: true, subtree: true };
    return new Promise(resolve => {
      if (document.querySelector(target)) {
        return resolve(document.querySelector(target) as Element);
      }

      const observer = new MutationObserver(_mutations => {
        const targetElement = document.querySelector(target);
        if (targetElement) {
          observer.disconnect();
          resolve(targetElement as Element);
        }
      });

      observer.observe(document.documentElement, config);
    });
  }
}
